import { useState, type ReactNode } from 'react'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { OverrideSection } from '@/features/report/reconcile/OverrideSection'
import {
  CodeBadge,
  OverrideMarker,
  Section,
  SimpleTable,
  StatusBadge,
} from '@/features/report/reconcile/parts'
import { orUnknown, tartiNoText, ton } from '@/features/report/reconcile/format'
import { formatCubeDateTime, formatDateOnly, formatInteger, formatText } from '@/lib/format'
import {
  U_H_NOTE,
  type DiaRow,
  type IssueCode,
  type KantarRow,
  type ReconcileRecord,
} from '@/lib/kantar-dia'
import { messages } from '@/lib/messages'

const t = messages.reconcile
const d = t.detail
const col = t.columns

interface CompareField {
  label: string
  kantar: (row: KantarRow) => string | null
  dia: (row: DiaRow) => string | null
  /** Bu alandaki farkı bildiren kural; `note` aynı kodun hangi alana ait olduğunu ayırır. */
  code?: IssueCode
  note?: string
}

/** `compareMlzm`'deki karşılaştırmalar, aynı sırayla. */
const COMPARE_FIELDS: CompareField[] = [
  {
    label: d.fields.tarih,
    kantar: (row) => formatDateOnly(row.tarih),
    dia: (row) => formatDateOnly(row.tarih),
    code: 'U-I',
  },
  {
    label: d.fields.cariKod,
    kantar: (row) => row.cariKod,
    dia: (row) => row.cariKod,
    code: 'H-D',
  },
  { label: d.fields.cari, kantar: (row) => row.cariAdi, dia: (row) => row.cariUnvan },
  {
    label: d.fields.stokKod,
    kantar: (row) => row.stokKod,
    dia: (row) => row.stokKod,
    code: 'H-E',
  },
  { label: d.fields.plaka, kantar: (row) => row.plaka, dia: (row) => row.plaka, code: 'H-F' },
  {
    label: d.fields.siparis,
    kantar: (row) => row.siparisNo,
    dia: (row) => row.siparisBelgeNo,
    code: 'U-G',
  },
  {
    label: d.fields.sofor,
    kantar: (row) => row.sofor,
    dia: (row) => row.surucu,
    code: 'U-H',
    note: U_H_NOTE.driver,
  },
  {
    label: d.fields.nakliyeci,
    kantar: (row) => row.nakliyeci,
    dia: (row) => row.nakliyeciUnvan,
    code: 'U-H',
    note: U_H_NOTE.carrier,
  },
]

function flagged(record: ReconcileRecord, field: Pick<CompareField, 'code' | 'note'>) {
  return record.issues.some(
    (issue) => issue.code === field.code && (field.note === undefined || issue.note === field.note),
  )
}

function IssueList({ record }: { record: ReconcileRecord }) {
  if (record.issues.length === 0) {
    return <p className="text-muted-foreground p-4 text-sm">{t.noIssues}</p>
  }
  return (
    <ul className="divide-border divide-y">
      {record.issues.map((issue, index) => (
        <li key={index} className="flex flex-col gap-1 px-4 py-3 text-sm">
          <span className="flex flex-wrap items-center gap-2">
            <CodeBadge code={issue.code} />
            {issue.note && <span className="text-muted-foreground">{issue.note}</span>}
          </span>
          {(issue.kantar !== undefined || issue.dia !== undefined) && (
            <span className="text-muted-foreground">
              {col.kantarValue}: <span className="text-foreground">{orUnknown(issue.kantar)}</span>
              {' · '}
              {col.diaValue}: <span className="text-foreground">{orUnknown(issue.dia)}</span>
              {issue.diff && (
                <>
                  {' · '}
                  {col.diff}: <span className="text-foreground tabular">{issue.diff}</span>
                </>
              )}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}

function CompareTable({ record }: { record: ReconcileRecord }) {
  const kantar = record.kantar
  const mlzm = record.lines.find((line) => line.kalemTuru === 'MLZM')
  const cell = (value: string | null | undefined): ReactNode => formatText(value ?? null)
  // Farklı alanın kodu adıyla alan adının altında: telefonda dört kolon sığmıyordu.
  const label = (text: string, field: Pick<CompareField, 'code' | 'note'>) => (
    <span className="flex flex-col items-start gap-1">
      <span>{text}</span>
      {field.code && flagged(record, field) && <CodeBadge code={field.code} />}
    </span>
  )

  const rows: ReactNode[][] = COMPARE_FIELDS.map((field) => [
    label(field.label, field),
    cell(kantar && field.kantar(kantar)),
    cell(mlzm && field.dia(mlzm)),
  ])
  rows.push([
    label(d.fields.net, { code: 'H-C' }),
    kantar ? ton(record.kantarTon) : formatText(null),
    mlzm ? ton(record.diaTon) : formatText(null),
  ])
  return <SimpleTable head={[d.field, d.kantar, d.dia]} rows={rows} />
}

function WeighingTable({ kantar }: { kantar: KantarRow }) {
  return (
    <SimpleTable
      head={[d.field, d.kantar]}
      rows={[
        [d.fields.surec, formatText(kantar.surecDurumu)],
        [d.fields.giris, formatCubeDateTime(kantar.giris)],
        [d.fields.cikis, formatCubeDateTime(kantar.cikis)],
        [
          d.fields.girisKg,
          kantar.girisKg === null ? formatText(null) : formatInteger(kantar.girisKg),
        ],
        [
          d.fields.cikisKg,
          kantar.cikisKg === null ? formatText(null) : formatInteger(kantar.cikisKg),
        ],
      ]}
    />
  )
}

/** Satır başına küçük blok: yedi kolonluk tablo panelde (özellikle telefonda) sığmıyordu. */
function LinesList({ lines }: { lines: DiaRow[] }) {
  if (lines.length === 0) return <p className="text-muted-foreground p-4 text-sm">{d.noLines}</p>
  const c = d.lineColumns
  return (
    <ul className="divide-border divide-y">
      {lines.map((line, index) => (
        <li key={index} className="flex flex-col gap-0.5 px-4 py-3 text-sm">
          <span className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className="font-medium">
              {formatText(line.irsaliyeTuru)} · {formatText(line.kalemTuru)}
            </span>
            <span className="tabular">
              {ton(line.miktar)} {c.unit}
            </span>
          </span>
          <span>{formatText(line.cariUnvan)}</span>
          <span className="text-muted-foreground text-xs">
            {c.irsaliyeNo}: {formatText(line.irsaliyeNo)} · {c.stokKod}: {formatText(line.stokKod)}{' '}
            · {c.prefix}: {line.tartiOnek ?? ''} / {line.firmaKodu ?? ''}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** Tek tartının tüm ayrıntısı. `record` yoksa panel kapalıdır. */
export function RecordDetailSheet({
  record,
  onClose,
}: {
  record: ReconcileRecord | undefined
  onClose: () => void
}) {
  // Kapanış animasyonu boyunca son kayıt görünür kalsın.
  const [last, setLast] = useState(record)
  if (record && record !== last) setLast(record)
  const shown = record ?? last

  return (
    <Sheet open={record !== undefined} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-2xl">
        {shown && (
          <>
            <SheetHeader>
              <SheetTitle>
                {shown.kantarAdi} · {tartiNoText(shown.tartino)}
              </SheetTitle>
              <SheetDescription className="flex flex-wrap items-center gap-2 text-sm">
                <StatusBadge status={shown.status} />
                {shown.override && <OverrideMarker record={shown} />}
                <span>{formatDateOnly(shown.tarih)}</span>
                <span>{orUnknown(shown.plaka)}</span>
                <span>{orUnknown(shown.cari)}</span>
              </SheetDescription>
            </SheetHeader>
            <div className="flex flex-col gap-4 overflow-y-auto p-3 sm:p-4">
              <Section title={d.issues}>
                <IssueList record={shown} />
              </Section>
              <Section title={t.override.title}>
                <OverrideSection key={shown.key} record={shown} />
              </Section>
              <Section title={d.compare}>
                <CompareTable record={shown} />
              </Section>
              <Section title={d.weighing}>
                {shown.kantar ? (
                  <WeighingTable kantar={shown.kantar} />
                ) : (
                  <p className="text-muted-foreground p-4 text-sm">{d.noKantar}</p>
                )}
              </Section>
              <Section title={d.lines(shown.lines.length)}>
                <LinesList lines={shown.lines} />
              </Section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
